// Color science for finish reasoning.
//
// WHY HEX IS NOT ENOUGH (the fix for hex-to-paint matching approaches):
// 1. Hex/RGB is device-referred and perceptually non-uniform. Two hexes
//    that look "close" numerically can be visibly different, and vice
//    versa. We convert to CIELAB and compare with Delta E, which tracks
//    human perception.
// 2. Hex carries no LRV. LRV (light reflectance value) is measured by the
//    manufacturer and drives the low-light and small-room rules. We store
//    published LRV rather than inferring it from hex.
// 3. Hex carries no undertone. Undertone family (pink/peach/yellow/green/
//    blue/violet/gray-true) is what makes greiges clash or harmonize, and
//    it only reveals itself against neighbors and under specific light.
//    We classify from Lab a*/b* geometry and allow manual override from
//    fan-deck expertise.
// 4. Hex ignores metamerism and illumination. The same pigment shifts
//    under 2700K vs 4000K bulbs and north vs south light. We model light
//    as a modifier on effective temperature, not a property of the color.
// 5. Hex ignores sheen entirely. Sheen changes specular reflection, so a
//    semi-gloss reads deeper and shows more surface flaws than the same
//    color in matte. Sheen lives on the PRODUCT VARIANT axis (durability +
//    specular character), never inside color identity.

export interface Lab {
  L: number;
  a: number;
  b: number;
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

// sRGB -> XYZ (D65) -> CIELAB
export function hexToLab(hex: string): Lab {
  let [r, g, b] = hexToRgb(hex).map((v) => v / 255);
  const lin = (c: number) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  r = lin(r); g = lin(g); b = lin(b);

  const X = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
  const Y = r * 0.2126 + g * 0.7152 + b * 0.0722;
  const Z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;

  const f = (t: number) =>
    t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116;
  const fx = f(X), fy = f(Y), fz = f(Z);

  return {
    L: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

// CIE76 Delta E: adequate for near-neutral interior palettes at MVP.
// Upgrade path: CIEDE2000 when the catalog includes saturated colors.
export function deltaE(c1: Lab, c2: Lab): number {
  return Math.sqrt(
    (c1.L - c2.L) ** 2 + (c1.a - c2.a) ** 2 + (c1.b - c2.b) ** 2
  );
}

// Rough perceptual bands for user-facing language:
// dE < 1 imperceptible, 1-2 trained eye, 2-5 noticeable side by side,
// 5-10 clearly different, >10 different colors.
export function deltaEBand(dE: number): string {
  if (dE < 1) return "imperceptible";
  if (dE < 2) return "trained-eye";
  if (dE < 5) return "side-by-side";
  if (dE < 10) return "clearly-different";
  return "different-colors";
}

export type UndertoneFamily =
  | "pink" | "peach" | "yellow" | "green" | "blue" | "violet" | "gray-true";

// Classify undertone from Lab chromatic geometry. Fan-deck data (manual
// classification in the seed catalog) always wins; this is the fallback
// for arbitrary hex input from users.
export function classifyUndertone(lab: Lab): UndertoneFamily {
  const { a, b } = lab;
  const chroma = Math.hypot(a, b);
  if (chroma < 2.5) return "gray-true";
  const hue = (Math.atan2(b, a) * 180) / Math.PI; // -180..180
  if (hue >= 20 && hue < 70) return a > 6 ? "peach" : "yellow";
  if (hue >= 70 && hue < 110) return "yellow";
  if (hue >= 110 && hue < 180) return "green";
  if (hue >= -180 && hue < -100) return "blue";
  if (hue >= -100 && hue < -20) return "violet";
  return "pink"; // warm red zone
}

// Effective temperature after lighting. Pigment temperature is -1..+1;
// light shifts perception. North light and cool bulbs push cooler; south
// light and 2700K bulbs push warmer.
export function effectiveTemperature(
  pigmentTemp: number,
  orientation: string,
  bulbCCT: number
): number {
  let shift = 0;
  if (orientation === "north") shift -= 0.15;
  if (orientation === "south") shift += 0.1;
  if (orientation === "west") shift += 0.05;
  shift += bulbCCT <= 2700 ? 0.1 : bulbCCT >= 4000 ? -0.12 : 0;
  return Math.max(-1, Math.min(1, pigmentTemp + shift));
}

// Sheen ladder: ordered from least to most reflective. Used by S-01/S-02.
export const SHEEN_LADDER = [
  "flat", "matte", "eggshell", "pearl", "satin", "semi-gloss", "gloss",
] as const;
export type Sheen = (typeof SHEEN_LADDER)[number];

export function sheenStep(s: Sheen): number {
  return SHEEN_LADDER.indexOf(s);
}
